import { Grid } from "@mui/material";
import React, { useState } from "react";
import { FC } from "react";
import { TopLevelPaper } from "../../../../../../themes/StyledComponents";
import useStore from "../../../../../../helpers/useStore";
import BillBoxWrapper from "../../../DirectBilling/common/components/BillBoxWrapper";
import { observer } from "mobx-react";
import {
  BillingTypeEnum,
  ScreenMode,
} from "../../../Base/types/insuranceBills.types";
import { ExportBillingData, LineItemDto } from "../../../../../../api-client";
import { TransactionsContext } from "../../../../../../stores/Transactions/Transactions.provider";
import { EditLineItemCorrectionBillFullScreen } from "../../Edit/exports";
import LineItemCorrectionBillInfo from "../components/LineItemCorrectionBillInfo";
import LineItemSelector from "../components/sub-components/LineItemSelector";

interface NewLineItemCorrectionBillProps {
  handleFullScreenDialogClose: () => void;
  selectedAccount: string;
  userName: string;
  userId: string;
}

const NewLineItemCorrectionBill: FC<NewLineItemCorrectionBillProps> = observer(
  ({ handleFullScreenDialogClose, selectedAccount, userName, userId }) => {
    const [mode, setMode] = useState<ScreenMode>(ScreenMode.NEW);
    const [editBills, setEditBills] = useState<ExportBillingData[]>([]);

    const [selectedRx, setSelectedRx] = useState<any>(null);
    const [isVisible, setIsVisible] = useState(false);

    // Line item selections: rxId -> selected LineItemDto[]
    const [lineItemSelections, setLineItemSelections] = useState<
      Record<string, LineItemDto[]>
    >({});

    const transactionStore = useStore(TransactionsContext);
    const { getBillingDataForEditing } = transactionStore;

    const handleRxDelete = (deletedRxId: string) => {
      if (selectedRx && selectedRx.id === deletedRxId) {
        setSelectedRx(null);
        setIsVisible(false);
      }
      // Clean up line item selection
      setLineItemSelections((prev) => {
        const next = { ...prev };
        delete next[deletedRxId];
        return next;
      });
    };

    const handleRowClick = (rx: any) => {
      setSelectedRx(rx);
      setIsVisible(true);
    };

    const handleValidationFilter = (remainingRxs: any[]) => {
      if (selectedRx && !remainingRxs.some((rx) => rx.id === selectedRx.id)) {
        setSelectedRx(null);
        setIsVisible(false);
      }
    };

    const handleLineItemSelectionChange = (
      rxId: string,
      selectedItems: LineItemDto[],
    ) => {
      setLineItemSelections((prev) => ({
        ...prev,
        [rxId]: selectedItems,
      }));
    };

    const handleBillsSaved = async (ids: string[]) => {
      const bills = await getBillingDataForEditing(
        ids,
        BillingTypeEnum.LINE_ITEM_CORRECTION,
      );
      setEditBills(bills);
      setMode(ScreenMode.EDIT);
    };

    /* ---------  render  --------- */

    if (mode === ScreenMode.EDIT) {
      return (
        <EditLineItemCorrectionBillFullScreen
          handleFullScreenDialogClose={() => {
            setMode(ScreenMode.NEW);
            setEditBills([]);
            handleFullScreenDialogClose?.();
          }}
          initialBills={editBills}
        />
      );
    }

    return (
      <Grid container spacing={1} sx={{ height: "100%", marginTop: "1px" }}>
        <BillBoxWrapper xs={5}>
          <TopLevelPaper className="scrollable" sx={{ paddingTop: 0 }}>
            <LineItemCorrectionBillInfo
              handleFullScreenDialogClose={handleFullScreenDialogClose}
              onRowClick={handleRowClick}
              account={selectedAccount}
              userName={userName}
              userId={userId}
              onRxDelete={handleRxDelete}
              onValidationFilter={handleValidationFilter}
              onBillsSaved={handleBillsSaved}
              selectedRx={selectedRx}
              lineItemSelections={lineItemSelections}
              onLineItemSelectionChange={handleLineItemSelectionChange}
            />
          </TopLevelPaper>
        </BillBoxWrapper>
        {isVisible && selectedRx && (
          <BillBoxWrapper xs={7}>
            <TopLevelPaper className="scrollable rounded-container">
              <LineItemSelector
                rxId={selectedRx.id}
                rxData={selectedRx}
                selectedLineItems={lineItemSelections[selectedRx.id] || []}
                onSelectionChange={(items) =>
                  handleLineItemSelectionChange(selectedRx.id, items)
                }
              />
            </TopLevelPaper>
          </BillBoxWrapper>
        )}
      </Grid>
    );
  },
);

export default NewLineItemCorrectionBill;
